import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {createGeometryValueRefData, createMeshGeometryAttributeData, createMeshGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeSetData, MeshGeometryAttributeData, MeshGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode, meshGeometryAttributeSetLike} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {meshGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import {getDisplaySubdivisionLevel, maxSubdivisionLimit} from "#template/nodes/object/mesh/mesh-node"
import type {SceneProperties} from "#template/nodes/render/scene-properties"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {AttributeRef, GeomBuilderContext} from "#template/geometry-processing/geometry-graph"
import {Operators, ValueRef} from "#template/geometry-processing/geometry-graph"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export const SUBDIVIDE_OPERATOR_OUTPUT_POSITION_ATTR = "SubdivideOperator.position"
export const SUBDIVIDE_OPERATOR_OUTPUT_NORMAL_ATTR = "SubdivideOperator.normal"
export const SUBDIVIDE_OPERATOR_OUTPUT_VERTEX_VARYING_ATTRS = "SubdivideOperator.vertexVaryingAttrs"
export const SUBDIVIDE_OPERATOR_OUTPUT_PRIMITIVE_UNIFORM_ATTRS = "SubdivideOperator.primitiveUniformAttrs"

// Single-variant operator (mesh-only): parameter type is concrete, no per-dim genericity.
export type SubdivideOperatorParameters = {
    name: string
    subdivisionLevel: NumberLike | null
    positionAttribute: MeshGeometryAttributeLikeNode | null
    vertexVaryingAttributes: MeshGeometryAttributeSetLikeNode | null
    primitiveUniformAttributes: MeshGeometryAttributeSetLikeNode | null
}

export const subdivideOperatorParametersSchema = z.object({
    name: z.string(),
    subdivisionLevel: z.nullable(numberLike),
    positionAttribute: z.nullable(meshGeometryAttributeLike),
    vertexVaryingAttributes: z.nullable(meshGeometryAttributeSetLike),
    primitiveUniformAttributes: z.nullable(meshGeometryAttributeSetLike),
})

function applySubdivideOperator(
    ctx: GeomBuilderContext,
    renderSubdivisionLevel: number,
    displaySubdivisionLevel: number,
    positionAttribute: MeshGeometryAttributeData,
    vertexVaryingAttributes: MeshGeometryAttributeSetData | null,
    primitiveUniformAttributes: MeshGeometryAttributeSetData | null,
): MeshGeometryAttributeSetData {
    const subdivisionLevel = createGeometryValueRefData(
        new ValueRef(ctx, renderSubdivisionLevel, "uint32"),
        new ValueRef(ctx, displaySubdivisionLevel, "uint32"),
        {},
    )
    const inPositionAttrRefData = positionAttribute.ref
    const inVertexVaryingAttrRefDataSet =
        vertexVaryingAttributes === null ? undefined : geometryAttributeSetDataToGeometryAttributeRefDataSet(vertexVaryingAttributes)
    const inPrimitiveUniformAttrRefDataSet =
        primitiveUniformAttributes === null ? undefined : geometryAttributeSetDataToGeometryAttributeRefDataSet(primitiveUniformAttributes)

    const subdividedAttrRefDataSet = applyGeometryOperator(
        (
            subdivisionLevel: ValueRef,
            inPositionAttrRef: AttributeRef,
            inVertexVaryingAttrRefSet: AttributeRefSet | undefined,
            inPrimitiveUniformAttrRefSet: AttributeRefSet | undefined,
        ) => {
            const [{position, normal}, vertexVaryingAttrs, primitiveUniformAttrs] = Operators.subdivide(
                subdivisionLevel,
                inPositionAttrRef,
                inVertexVaryingAttrRefSet,
                inPrimitiveUniformAttrRefSet,
            )

            return {
                position,
                normal,
                vertexVaryingAttrs,
                primitiveUniformAttrs,
            }
        },
        subdivisionLevel,
        inPositionAttrRefData,
        inVertexVaryingAttrRefDataSet,
        inPrimitiveUniformAttrRefDataSet,
    )

    return createMeshGeometryAttributeSetData({
        [`${SUBDIVIDE_OPERATOR_OUTPUT_POSITION_ATTR}`]: createMeshGeometryAttributeData(subdividedAttrRefDataSet.position),
        [`${SUBDIVIDE_OPERATOR_OUTPUT_NORMAL_ATTR}`]: createMeshGeometryAttributeData(subdividedAttrRefDataSet.normal),
        [`${SUBDIVIDE_OPERATOR_OUTPUT_VERTEX_VARYING_ATTRS}`]: geometryAttributeRefDataSetToGeometryAttributeSetData(
            subdividedAttrRefDataSet.vertexVaryingAttrs,
            2,
        ),
        [`${SUBDIVIDE_OPERATOR_OUTPUT_PRIMITIVE_UNIFORM_ATTRS}`]: geometryAttributeRefDataSetToGeometryAttributeSetData(
            subdividedAttrRefDataSet.primitiveUniformAttrs,
            2,
        ),
    })
}

export abstract class GeometrySubdivideOperatorNode<out P extends SubdivideOperatorParameters = SubdivideOperatorParameters> extends GeometryAttributeSetNode<
    2,
    P
> {
    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return parameters.subdivisionLevel !== null && parameters.positionAttribute !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
        sceneManager: ISceneManager,
        sceneProperties: SceneProperties | undefined,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<2> | null>; invalidator?: BuilderInlet<null>} {
        const {subdivisionLevel, positionAttribute, vertexVaryingAttributes, primitiveUniformAttributes} = parameters

        const [evaluatedSubdivisionLevelValid, evaluatedSubdivisionLevelInvalid] = scope.branch(evaluator.evaluate(subdivisionLevel))
        const [evaluatedPositionAttributeValid, evaluatedPositionAttributeInvalid] = scope.branch(evaluator.evaluate(positionAttribute))
        const evaluatedVertexVaryingAttributes = vertexVaryingAttributes === null ? null : evaluator.evaluate(vertexVaryingAttributes)
        const evaluatedPrimitiveUniformAttributes = primitiveUniformAttributes === null ? null : evaluator.evaluate(primitiveUniformAttributes)

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(
                ctx,
                evaluatedSubdivisionLevelValid,
                evaluatedPositionAttributeValid,
                evaluatedVertexVaryingAttributes,
                evaluatedPrimitiveUniformAttributes,
                sceneProperties,
                sceneManager,
            ),
            ([ctx, subdivisionLevel, positionAttributeData, vertexVaryingAttributesData, primitiveUniformAttributesData, sceneProperties, sceneManager]) => {
                const renderSubdivisionLevel = Math.min(subdivisionLevel, maxSubdivisionLimit)
                const displaySubdivisionLevel = getDisplaySubdivisionLevel(
                    subdivisionLevel,
                    sceneProperties?.parameters.maxSubdivisionLevelOnMobile,
                    sceneProperties?.parameters.maxSubdivisionLevel,
                    sceneManager,
                )

                return applySubdivideOperator(
                    ctx,
                    renderSubdivisionLevel,
                    displaySubdivisionLevel,
                    positionAttributeData,
                    vertexVaryingAttributesData,
                    primitiveUniformAttributesData,
                )
            },
            "applySubdivideOperator",
        )

        return {evaluatedGeometryData: geometryAttributeSetData, invalidator: scope.phi(evaluatedSubdivisionLevelInvalid, evaluatedPositionAttributeInvalid)}
    }
}
